import { motion } from 'framer-motion';
import { useState } from 'react';
import { useInView } from 'react-intersection-observer';

interface VSLSectionProps {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const VSLSection = ({ onMouseEnter, onMouseLeave }: VSLSectionProps) => {
  const [loadVideo, setLoadVideo] = useState(false);
  const { ref } = useInView({
    triggerOnce: true,
    threshold: 0.1,
    onChange: (visible) => {
      if (visible) setLoadVideo(true);
    }
  });

  return (
    <div className="bg-dark text-white w-full 2xl:px-52 px-0 py-16 md:py-24 lg:py-32">
      <div className="lg:px-24 px-6 md:px-8">
        <div className="w-full flex flex-col items-center" id="About">
          
          {/* "Watch this" text with arrow - centered above video */}
          <motion.div
            className="flex flex-col items-center text-pink mb-6 md:mb-8"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.8 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sm md:text-base font-medium italic mb-2">Watch this</span>
            <motion.svg
              className="w-6 md:w-8"
              viewBox="0 0 40 40"
              fill="none"
            >
              <motion.path
                d="M20 5 L 20 30"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true, amount: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              />
              <motion.path
                d="M12 22 L 20 30 L 28 22"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true, amount: 1 }}
                transition={{ duration: 0.3, delay: 0.5 }}
              />
            </motion.svg>
          </motion.div>

          {/* Video container with squiggles */}
          <div className="xl:w-4/5 w-full relative" ref={ref}>
            
            {/* Top-left corner squiggle */}
            <motion.svg
              className="absolute -top-6 -left-6 md:-top-8 md:-left-8 w-12 md:w-16 text-pink z-10"
              viewBox="0 0 50 50"
              fill="none"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <motion.path
                d="M45 5 Q 25 5, 25 25 Q 25 45, 5 45"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                fill="none"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.5 }}
              />
            </motion.svg>

            {/* Top-right corner squiggle */}
            <motion.svg
              className="absolute -top-6 -right-6 md:-top-8 md:-right-8 w-12 md:w-16 text-pink z-10"
              viewBox="0 0 50 50"
              fill="none"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.4 }}
            >
              <motion.path
                d="M5 5 Q 25 5, 25 25 Q 25 45, 45 45"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                fill="none"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.6 }}
              />
            </motion.svg>

            {/* Bottom-left corner squiggle */}
            <motion.svg
              className="absolute -bottom-6 -left-6 md:-bottom-8 md:-left-8 w-12 md:w-16 text-pink z-10"
              viewBox="0 0 50 50"
              fill="none"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.5 }}
            >
              <motion.path
                d="M45 45 Q 25 45, 25 25 Q 25 5, 5 5"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                fill="none"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.7 }}
              />
            </motion.svg>

            {/* Bottom-right corner squiggle */}
            <motion.svg
              className="absolute -bottom-6 -right-6 md:-bottom-8 md:-right-8 w-12 md:w-16 text-pink z-10"
              viewBox="0 0 50 50"
              fill="none"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.6 }}
            >
              <motion.path
                d="M5 45 Q 25 45, 25 25 Q 25 5, 45 5"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                fill="none"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.8 }}
              />
            </motion.svg>

            {/* Glow effect behind video */}
            <div className="absolute inset-0 bg-pink/10 blur-3xl rounded-3xl -z-10" />
            
            {/* Video - lazy loaded when in view */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="w-full aspect-[16/9] rounded-lg bg-zinc-900 overflow-hidden"
            >
              {loadVideo ? (
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/QMVprMjUny8?autoplay=1&mute=1"
                  title="Make Your UGC Game Go Viral on Shorts"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  loading="lazy"
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                />
              ) : (
                <div 
                  className="w-full h-full flex items-center justify-center cursor-pointer group"
                  onClick={() => setLoadVideo(true)}
                >
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-pink rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 md:w-8 md:h-8 text-white ml-1" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VSLSection;